import React, { useState, useRef } from "react";



import "./contentpage.css";
import Question from "../components/Question";
import FloatingPageNav from "../components/FloatingPageNav";
import LandingSection from "./LandingSection";

function IslamicTradePage7() {
  const contentRef = useRef(null);
     const links = {
    prev: "/islamic-trade-page-6", 
    index: "/index",                 
    next: "/islamic-trade-page-8", 
  };
  const titleText = "Types of Usury (Ribā)";
  const questions = [
    {
      text: "1. Which of the following best describes Riba al-Fadl?",
      options: [
        {
          value: "A",
          label: "A.  Interest charged on a delayed loan repayment.",
        },
        {
          value: "B",
          label: "B. Unequal exchange of usurious commodities of the same type",
        },
        {
          value: "C",
          label:
            "C. Selling a commodity at a higher price with delayed payment",
        },
        {
          value: "D",
          label: "D. Loaning money without expecting any return",
        },
      ],
      correct: "B",
    },
    {
      text: "2. Which of the following is NOT one of the six commodities mentioned in the hadith on Riba?",
      options: [
        { value: "A", label: "A. Gold" },
        { value: "B", label: "B. Salt" },
        { value: "C", label: "C. Olive oil" },
        { value: "D", label: "D. Dates" },
      ],
      correct: "C",
    },
    {
      text: "3. Riba an-Nasee'ah is prohibited in Islam because it:",
      options: [
        { value: "A", label: "A. Encourages barter trade" },
        { value: "B", label: "B. Promotes equality in pricing" },
        {
          value: "C",
          label:
            "C. Leads to exploitation by charging interest for delayed repayment",
        },
        {
          value: "D",
          label: "D. Involves physical possession of goods at the time of sale",
        },
      ],
      correct: "C",
    },
    {
      text: "4. According to the hadith in Sahih Muslim 1587, what condition must be fulfilled when exchanging the same type of ribawi commodity (e.g., gold for gold)?",
      options: [
        {
          value: "A",
          label: "A. The transaction must be in the form of a loan",
        },
        {
          value: "B",
          label: "B. One party must benefit more due to quality difference",
        },
        { value: "C", label: "C. It must be unequal but with mutual consent" },
        {
          value: "D",
          label: "D. It must be equal in quantity and exchanged hand-to-hand",
        },
      ],
      correct: "D",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);
  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={7}
        timerMinutes={60}
          currentLevel={1}
      />

      <p className="content" ref={contentRef}>
        Welcome to <b>Day 7</b> of our learning journey. As we delve deeper into
        the moral and structural foundations of Islamic finance, today’s session
        introduces two critical concepts i.e Riba al-Fadl and Riba an-Nasee'ah.
        These are not just abstract legal terms but essential pillars in
        understanding how Islam seeks to uphold fairness, transparency, and
        justice in economic transactions. Let us approach this topic with
        clarity, sincerity, and an open heart, aiming to align our understanding
        of trade and finance with the timeless wisdom of the Shariah.
      </p>
      <p className="content">
        As we journey deeper into the study of Islamic Financial Jurisprudence,
        it becomes increasingly clear that the prohibition of Riba is not a mere
        restriction on certain monetary practices but a divine boundary that
        preserves justice, balance, and compassion in economic life. Today, we
        explore two particular forms of Riba that are vital for understanding
        how Islam maintains fairness and integrity in financial dealings: Riba
        al-Fadl and Riba an-Nasee’ah. These are not abstract terms confined to
        ancient history; they are living principles that shape how Muslims
        should approach exchange, trade, and lending even in today’s digital
        economy.
        <br />
        The root word of Riba means “increase” or “growth.” However, in Islamic
        law, it refers specifically to an increase that is unlawful i.e one that
        occurs without corresponding effort, risk, or value addition. Islam
        encourages trade, which is based on mutual consent and shared risk, but
        condemns any transaction that creates one-sided gain at the expense of
        another. The Qur’an draws this distinction clearly when Allah says:
        <br />
        “Allah has permitted trade and forbidden Riba”{" "}
        <a href="https://quran.com/2/275" target="blank">
          (Surah Al Baqarah 2:275)
        </a>
        .<br />
        This verse lays down the foundation of Islamic commerce which is based
        on legitimate growth through fair trade and cooperation and not on
        increase of wealth through exploitation and imbalance.
        <br />
        Riba al-Fadl and Riba an-Nasee’ah form the two main branches of this
        forbidden increase. The first arises when there is inequality in the
        exchange of similar commodities, while the second arises from a
        time-based increase in debt repayment. Together, they encompass the
        moral and structural principles Islam sets to prevent financial
        injustice and protect the weak from being exploited. These concepts are
        not limited to the marketplaces of medievial Arabia; their wisdom
        transcends time. They remain as relevant today as ever, especially when
        the global financial system has normalized interest, speculation, and
        artificial inflation.
        <br />
        Before exploring these two forms of Riba, it is crucial to understand
        why Allah and His Messenger were so stern in warning against usury. In
        one of the most fearsome verses in the Qur’an, Allah declares:
        <br />
        “O believers! Fear Allah, and give up outstanding interest if you are
        ˹true˺ believers. If you do not, then beware of a war with Allah and His
        Messenger! But if you repent, you may retain your principal neither
        inflicting nor suffering harm.”{" "}
        <a href="https://quran.com/2/278" target="blank">
          (Surah Al-Baqarah 2:278–279)
        </a>
        .<br />
        No other sin in Islam is described as inviting war from Allah and his
        Messneger(peace be upon him). This shows that Riba is not simply a
        matter of numbers and transactions; it is a moral violation that
        corrodes the fabric of society, spreading injustice and spiritual decay.
        <br />
        Thus, when Muslims study Riba al-Fadl and Riba an-Nasee’ah, they are not
        merely learning financial regulations. They are learning the ethical
        architecture of Islamic civilization, a system that safeguards fairness,
        transparency, and collective well-being.
        <br />
        <br />
        <b>Riba al-Fadl – Inequality in Exchange</b>
        <br />
        <br />
        Riba al-Fadl refers to the excess or surplus that occurs in a direct
        exchange of similar commodities. The Prophet Muhammad (peace and
        blessings be upon him) clarified this through one of the most important
        hadiths on economic ethics. He said:
        <br />
        “Gold is to be paid for by gold, silver by silver, wheat by wheat,
        barley by barley, dates by dates, salt by salt, like by like, payment
        being made hand to hand. He who made an addition to it, or asked for an
        addition, in fact dealt in usury. The receiver and the giver are equally
        guilty.”{" "}
        <a href="https://sunnah.com/muslim:1584e" target="blank">
          (Sahih Muslim 1587)
        </a>
        . This hadith forms the basis of Islamic monetary equivalence. It
        teaches that when exchanging usurious items of the same kind, they must
        be equal in quantity and exchanged immediately. Any inequality or delay
        transforms the transaction into one of Riba al-Fadl. This principle is
        not confined to these six commodities alone; scholars interpret them as
        representing categories gold and silver as mediums of exchange (money),
        and the four food items as consumables essential for human life. Hence,
        any commodity that shares the same effective cause (illah) such as
        currency, staple food, or precious metals must follow the same rule as
        per the opinion of many scholars.
        <br />
        During the Prophet’s time, barter trade was common. Traders often
        attempted to gain extra profit by exchanging unequal amounts of the same
        commodity. For instance, someone might trade two measures of
        lower-quality dates for one measure of high-quality dates. While this
        might appear fair by worldly reasoning, Islam forbids it because it
        opens the door to exploitation and subjective valuation. <br />
        It is reported in a hadith that when a man came to Medina, he brought
        with him dates called Janib. The Prophet (peace be upon him) asked him,
        "Are all the dates of Khaibar of this kind?" The man replied, "(No), we
        exchange two Sa's(unit) of bad dates for one Sa of this kind of dates
        (i.e. Janib), or exchange three Sa's for two." On that, the Prophet
        said, "Don't do so, as it is a kind of usury (Riba) but sell the dates
        of inferior quality for money, and then buy Janib with the money".
        <a href="https://sunnah.com/bukhari:2302" target="blank">
          (Sahih Al-Bukhari 2302)
        </a>
        <br /> The Prophet (peace be upon him) forbade the trader from directly
        exchanging dates of superior quality with those which are inferior, and
        instead instructed him to sell both the dates sperately in two different
        transactions and then use the money to purchase their prefered dates as
        this ensures that all transactions are conducted at fair market price.
        The Prophet’s ruling ensures that the market remains transparent and no
        one is cheated under the guise of quality difference.
        <br />
        The wisdom behind this prohibition is profound. Riba al-Fadl prevents
        economic manipulation and monopolistic practices that can harm
        vulnerable participants in trade. When traders exchange unequal goods,
        it becomes easy for the wealthy and the skilled to take advantage of
        those with lesser knowledge or bargaining power. Islam’s insistence on
        equivalence preserves fairness and mutual respect. It reminds humanity
        that trade must be a tool of cooperation, not domination.
        <br />
        In contemporary terms, Riba al-Fadl applies to many transactions. When
        exchanging foreign currencies, for instance, both the exchange and
        settlement must occur immediately to avoid deferment-based gain. If
        someone trades 100 USD for 90 EUR but delays settlement to a later date,
        the transaction risks falling into Riba al-Fadl, depending on its
        structure. Similarly, barter exchanges in which unequal amounts of
        commodities are traded, or cases where goods are exchanged at
        manipulated valuations, fall under this prohibition. Even pawnshops or
        gold traders who undervalue an item in exchange for instant cash are
        effectively engaging in Riba al-Fadl because they exploit asymmetry in
        information and value.
        <br />
        <br />
        Islamic finance offers lawful alternatives that embody transparency and
        consent. For example, a trader can sell one commodity for another at a
        mutually agreed price in a separate transaction, rather than exchanging
        the same commodity unequally. Islam allows trade but closes all doors
        that could lead to unfair gain without corresponding effort or risk. The
        Prophet (peace be upon him) emphasized this balance when he said:
        <br />
        “Leave what makes you in doubt for what does not make you in doubt. The
        truth brings tranquility while falsehood sows doubt.”{" "}
        <a href="https://sunnah.com/tirmidhi:2518">(Tirmidhi 2518)</a>.<br />
        In trade, this means avoiding any structure that could resemble usury or
        deceit. Beyond its legal implications, Riba al-Fadl carries deep moral
        lessons. It teaches moderation, contentment, and honesty. The believer
        who adheres to these principles nurtures a heart free from greed and
        deceit. He learns that true prosperity lies in fairness and barakah, not
        in manipulative gain. Thus, the prohibition of Riba al-Fadl is both an
        economic and spiritual safeguard, protecting the believer’s wealth and
        soul from corruption.
        <br />
        <br />
        <b>Riba an-Nasee’ah – The Corruption of Time-Based Interest</b>
        <br />
        <br />
        Riba an-Nasee’ah refers to the increase that arises from a deferred
        payment or delayed settlement of debt. The word “Nasee’ah” comes from
        the Arabic root meaning “to postpone” or “to defer.” In pre-Islamic
        Arabia, this was the most prevalent form of usury. Wealthy lenders would
        give loans to the poor, and when repayment became due, they would offer
        an extension but only in exchange for an increase in the owed amount.
        This was a system of exploitation that crushed the poor under the weight
        of growing debt. It institutionalized inequality and reduced compassion
        to a business contract.
        <br />
        Islam decisively uprooted this injustice. Allah says in the Qur’an: “O
        believers! Do not consume interest, multiplying it many times over. And
        be mindful of Allah, so you may prosper.”
        <a href="https://quran.com/3/130" target="_blank">
          (Surah Ali ‘Imran 3:130)
        </a>{" "}
        .<br />
        Another verse commands: “If it is difficult for someone to repay a debt,
        postpone it until a time of ease. And if you waive it as an act of
        charity, it will be better for you, if only you knew.”
        <a href="https://quran.com/2/280" target="_blank">
          (Surah Al-Baqarah 2:280)
        </a>{" "}
        .<br />
        Here, Allah establishes that the proper response to a debtor’s
        difficulty is mercy, not exploitation. The lender is encouraged to grant
        time or even forgive the debt, acts that are rewarded manifold in the
        Hereafter.
        <br />
        The Prophet Muhammad (peace be upon him) condemned all forms of deferred
        interest.
        <br />
        He said:“Every loan that draws a benefit is Riba” (Sunan al-Kubra lil
        Bayhaqi 10243). <br />
        This includes any contractual or conditional advantage that benefits the
        lender beyond the principal amount, whether in the form of cash,
        service, or favor. Even the smallest conditional increase transforms a
        benevolent act of lending into an act of Riba. The essence of this
        prohibition lies in separating charity from greed, and compassion from
        coercion.
        <br />
        In modern economies, Riba an-Nasee’ah manifests on a massive scale.
        Conventional banking, credit card debt, payday loans, and mortgage
        systems are built on time-based interest. When a borrower fails to pay
        on time, the debt multiplies through compounding interest. This cycle
        creates modern slavery, trapping individuals and entire nations in
        perpetual debt. The 2008 global financial crisis, for instance, was a
        direct outcome of excessive reliance on credit and interest-based
        instruments. When people and institutions borrow what they cannot repay,
        the system collapses under its own immorality.
        <br />
        The consequences of this form of Riba go beyond economics. It corrodes
        society’s moral foundation. When money itself becomes a product that
        earns more money without labor or risk, the relationship between capital
        and human effort is severed. The wealthy grow richer not through
        innovation or productivity but through the suffering of the indebted.
        This widens the gap between rich and poor, erodes empathy, and fosters
        materialism. The Prophet (peace be upon him) foretold that such times
        would come.
        <br />
        He said: “A time will come upon the people when they will consume Riba.”
        The companions asked, “All of them, O Messenger of Allah?” He said,
        “Whoever does not consume it will be affected by its dust”{" "}
        <a href="https://sunnah.com/ibnmajah:2278" target="blank">
          (Ibn Majah 2278)
        </a>
        .<br />
        Indeed, in today’s world, nearly every economy, including those of
        Muslim nations, operates upon interest-bearing systems. Central banks
        issue fiat currency backed by debt, and governments borrow money with
        interest to finance expenditure. The entire monetary ecosystem, from
        corporate bonds to credit cards, is entangled in Riba. This widespread
        entrenchment of usury in economic structures is not only a moral crisis
        but also one of the signs of the end times, as mentioned in several
        hadiths. The Prophet (peace be upon him) said that among the signs of
        the Hour is the prevalence of usury, bribery, and immorality. When
        Muslims tolerate these practices, they drift further from divine justice
        and expose themselves to economic instability and spiritual ruin.
        <br />
        Islam’s alternative is Qard Hasan i.e the benevolent loan. In this
        model, a lender provides financial help without expecting any increase
        or benefit in return. The Qur’an describes it beautifully: <br />
        “Who will lend to Allah a good loan which Allah will multiply many times
        over? It is Allah ˹alone˺ who decreases and increases ˹wealth˺. And to
        Him you will ˹all˺ be returned.”{" "}
        <a href="https://quran.com/2/245" target="blank">
          (Surah Al-Baqarah 2:245)
        </a>
        . <br />
        This verse turns lending into an act of worship, an opportunity to earn
        eternal profit rather than temporal gain. Islam also promotes
        partnership models like Mudarabah and Musharakah, where profit and loss
        are shared fairly, aligning the incentives of both parties with justice
        and cooperation.
        <br />
        <br />
        <br />
        <b>The Way Forward</b>
        <br />
        <br />
        The wisdom behind the prohibition of both Riba al-Fadl and Riba
        an-Nasee’ah is timeless. Islam envisions an economy where wealth
        circulates fairly, where people trade goods and services of real value,
        and where financial growth arises from productivity and cooperation
        rather than exploitation. In contrast, the modern fiat monetary system
        is built upon debt creation and interest-based expansion. Central banks
        create money out of nothing and lend it at interest, leading to
        perpetual inflation. This system rewards debtors who create artificial
        credit and punishes savers whose wealth loses value over time. It is the
        embodiment of Riba an-Nasee’ah on a global scale.
        <br />
        Every Muslim nation today has tied its economy to this interest-based
        model. Governments issue bonds, take loans from international
        institutions, and operate banks that charge interest. This dependency
        enslaves them to global powers that control credit. The Qur’an warns
        that when a people persist in disobedience despite divine guidance, they
        are left to their own corruption. The prevalence of Riba in the Muslim
        world is both a cause and a symptom of spiritual decline. It reflects a
        civilization that has strayed from tawakkul i.e trust in Allah’s
        provision, and replaced it with trust in human institutions.
        <br />
        The solution lies in reviving the prophetic model of economy. Muslims
        must rebuild financial systems on principles of equity, partnership, and
        honesty. Communities can create cooperative funds, charitable lending
        circles, and Riba-free investment structures. Education plays a vital
        role in this transformation; understanding the spiritual and social
        harms of Riba can awaken conscience and empower action. The Qur’an calls
        believers to such awareness:
        <br />
        "O believers! Fear Allah, and give up outstanding interest if you are
        ˹true˺ believers."
        <a href="https://quran.com/2/278" target="blank">
          (Surah Al Baqarah 2:278)
        </a>
        <br /> True belief is demonstrated in action, in one’s willingness to
        abandon prohibited gain for the sake of divine pleasure.
        <br />
        The prohibition of Riba is not a hindrance to progress but a gateway to
        sustainable prosperity. When wealth is earned through lawful trade and
        shared risk, societies flourish with blessings that multiplies benefit
        in unseen ways. In contrast, interest-based systems may appear
        profitable in numbers but bring spiritual emptiness and economic
        collapse. <br />
        The Prophet (peace be upon him) said: "There is no one who deals in
        usury a great deal (to increase his wealth) but he will end up with
        little (i.e., his wealth will be decreased)."
        <a href="https://sunnah.com/ibnmajah:2279" target="blank">
          (Ibn Majah 2279)
        </a>{" "}
        . <br />
        This is evident today, as nations drowning in debt face inflation,
        inequality, and moral decay.
        <br />
        Thus, understanding Riba al-Fadl and Riba an-Nasee’ah is not merely an
        academic exercise; it is a spiritual awakening. It calls upon Muslims to
        purify their transactions, revive the ethics of trade, and rebuild their
        economies upon the foundation of divine justice. Every believer has a
        role whether as a consumer, entrepreneur, policymaker, or student to
        reject usury and strive for a fairer world guided by the principles of
        Islam.
        <br />
        The path forward requires courage and conviction. It may seem daunting
        in a world where Riba dominates every financial structure, but Allah’s
        promise stands firm:
        <br />
        “Whoever fears Allah, He will make for him a way out and provide for him
        from where he does not expect”{" "}
        <a href="https://quran.com/65/2" target="blank">
          (Surah At-Talaq 65:2)
        </a>
        .<br />
        When Muslims collectively choose faith over fear, halal over haram, and
        justice over convenience, they will find divine support in rebuilding an
        economy that honors human dignity and divine command.
        <br />
      </p>
      <p className="content">
        <b>Excersice</b>
        <br />
        <br />
        Let us understand the hadith that mentions the six commodities in which
        riba (usury/interest) is applicable which is found in Sahih Muslim.{" "}
        <br /> <br />
        The hadith states: "The Prophet (peace be upon him) said: 'Gold is to be
        exchanged for gold, silver for silver, wheat for wheat, barley for
        barley, dates for dates, and salt for salt, like for like, equal for
        equal, and hand-to-hand. If these commodities are increased or exchanged
        for something different, then sell as you wish, provided the exchange is
        hand-to-hand.'"{" "}
        <i>
          <a href="https://sunnah.com/muslim:1587c" traget="blank">
            (Sahih Muslim 1587)
          </a>
        </i>
        <br /> In this hadith, the six commodities are: Gold (gold for gold),
        Silver (silver for silver), Wheat (wheat for wheat), Barley (barley for
        barley), Dates (dates for dates), Salt (salt for salt). The Prophet
        (peace be upon him) outlines that these six items must be exchanged in
        equal amounts and in a hand-to-hand manner. If the exchange involves
        more or a different type of commodity, then riba does not apply, and the
        transaction can occur as long as it meets the conditions of fairness.
        <br /> Let us classify the types of commodities on which usury is
        applicale into two types for the sake of better understanding.
        <br />
        <br />
        <b>Type A :</b> This type includes edible commodities that are measured
        in weight or volume eg: Dates, Barley, Wheat and Salt.
        <br />
        <b>Type B :</b> This type includes monetary commodities against which
        the prices of other goods are measured eg Gold and Silver.
        <br />
        <br /> The below table enlists the types of possible trades involving
        usurious commodities and the types of usuries that can be applicale on
        those trade (if any).
      </p>
      <table className="trade-table ">
        <thead>
          <tr>
            <th>Type of Trade</th>
            <th>Usury Applicable</th>
          </tr>
          <tr>
            <td>
              A trade between two commodities of type A, such that commodities
              are of the same specie i.e selling dates in exchange of dates,
              then it is compulsory that the exchange should happen immediately
              and the quantity exchanged should be the same too.
              <br />
              Eg: Selling 1kg of dates for 1kg of dates is permissible as long
              as the possession is taken immediately.
            </td>
            <td>Both Usury of Surplus and Deferment</td>
          </tr>
          <tr>
            <td>
              A trade between two commodities of type A, such that commodities
              are of different species i.e Exchanging barley for wheat, then the
              exchange should happen immediately, however, disparity in quantity
              is permissible. <br />
              Eg: Selling 1kg of barley for 2kg of wheat is permissible as long
              as the possession is taken immediately.
            </td>
            <td>Usury of Deferment</td>
          </tr>
          <tr>
            <td>
              A trade between a commodity of type A with a commodity of type B
              i.e buying salt in exchange for gold, then it is permissible for
              the quantity of the commodities to differ as well as the
              possession can be deferred. <br />
              Eg: Buying 100kg of salt for 1gram of gold is permissible even if
              the payment is not made immediately.
            </td>
            <td>None</td>
          </tr>
          <tr>
            <td>
              A trade between two commodities of type B, such that commodities
              are of the same specie i.e selling gold for gold, then it is
              compulsory that the exchange should happen immediately and the
              quantity exchanged should be the same too. <br />
              Eg: Selling 1kg of gold for 1kg of gold is permissible as long as
              the possession is taken by the buyer and the seller immediately.
            </td>
            <td>Both Usury of Surplus and Deferment</td>
          </tr>
          <tr>
            <td>
              A trade between two commodities of type B, such that commodities
              are of different species i.e selling gold for silver, then the
              exchange should happen immediately, however, disparity in quantity
              is permissible.
            </td>
            <td>Only Usury of Deferment</td>
          </tr>
        </thead>
        <tbody></tbody>
      </table>
      <p className="content">
        <br />
        <b>Summary of Today's Lesson</b> <br />
        <br />
        Today we learned about two major forms of Riba: Riba al-Fadl and Riba
        an-Nasee’ah. Riba al-Fadl referred to unequal exchanges of the same type
        of commodity, such as trading a larger quantity of low-quality dates for
        a smaller amount of high-quality ones. We examined how such practices
        occurred in the time of the Prophet Muhammad (peace be upon him) and how
        he instructed fairness in transactions by requiring equal amounts and
        hand-to-hand exchanges. We also studied the theological reasons behind
        its prohibition, including the need to prevent exploitation, ensure
        fairness, and avoid unjust enrichment. Contemporary parallels were drawn
        from practices like unregulated currency exchange and unfair barter
        dealings. We then turned our attention to Riba an-Nasee’ah, which
        involved charging extra money for delayed repayments. This type of Riba
        was common in pre-Islamic Arabia and was strongly condemned in the
        Qur’an due to its exploitative nature. We saw how modern forms of this
        include interest on credit cards, payday loans, and conventional bank
        lending. Islam's alternative approaches, such as Qard Hasan, Mudarabah,
        and Musharakah, were highlighted as ethical financial models that align
        with Islamic principles. Lastly, we explored the six commodities
        mentioned in hadith gold, silver, wheat, barley, dates, and salt and
        learned how these items fall under specific rules of exchange. We
        concluded by categorizing them into edible goods and monetary standards
        to better understand the conditions that make certain transactions
        impermissible in Islam.<br/><br/>
        <b>Coming up next</b><br/><br/>
        In our next lesson, we will explore how Islamic law governs land ownership and the rights tied to its produce. From immovable structures to trees, crops, and naturally growing resources, we will uncover the principles that ensure fairness, clarity, and justice in property transactions. Day 8 will highlight the wisdom behind these rulings and how they protect effort, prevent disputes, and uphold communal welfare. Stay ready for another insightful step in our journey.<br/><br/>
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

        <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default IslamicTradePage7;
